import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

export interface Broker {
  id: number
  key: string
  label: string
  icon: string
  color: string
  bg_color: string
  text_color: string
  border_color: string
  created_at: string
  updated_at: string
}

interface BrokerState {
  brokers: Broker[]
  loadingBrokers: boolean
  brokersLoaded: boolean

  loadBrokers: (force?: boolean) => Promise<void>
  createBroker: (broker: Omit<Broker, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateBroker: (id: number, broker: Partial<Broker>) => Promise<void>
  deleteBroker: (id: number) => Promise<void>
  getBrokerByKey: (key: string) => Broker | undefined
}

export const useBrokerStore = create<BrokerState>()(
  devtools(
    (set, get) => ({
      brokers: [],
      loadingBrokers: false,
      brokersLoaded: false,

      loadBrokers: async (force = false) => {
        const { loadingBrokers, brokersLoaded } = get()
        if (!force && (brokersLoaded || loadingBrokers)) return

        set({ loadingBrokers: true })
        try {
          const response = await apiFetch('/api/brokers')
          if (!response.ok) throw new Error('Failed to fetch brokers')
          const data = await response.json()
          set({ brokers: data, brokersLoaded: true })
        } catch (error) {
          console.error('Error loading brokers:', error)
          toast.error('Failed to load brokers')
        } finally {
          set({ loadingBrokers: false })
        }
      },

      createBroker: async (broker) => {
        try {
          const response = await apiFetch('/api/brokers', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(broker),
          })
          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to create broker')
          }
          const newBroker = await response.json()
          set(state => ({ brokers: [...state.brokers, newBroker] }))
          toast.success('Broker created successfully')
        } catch (error: any) {
          console.error('Error creating broker:', error)
          toast.error(error.message || 'Failed to create broker')
          throw error
        }
      },

      updateBroker: async (id, broker) => {
        try {
          const response = await apiFetch(`/api/brokers/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(broker),
          })
          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update broker')
          }
          const updatedBroker = await response.json()
          set(state => ({
            brokers: state.brokers.map(b => b.id === id ? updatedBroker : b),
          }))
          toast.success('Broker updated successfully')
        } catch (error: any) {
          console.error('Error updating broker:', error)
          toast.error(error.message || 'Failed to update broker')
          throw error
        }
      },

      deleteBroker: async (id) => {
        try {
          const response = await apiFetch(`/api/brokers/${id}`, { method: 'DELETE' })
          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete broker')
          }
          set(state => ({ brokers: state.brokers.filter(b => b.id !== id) }))
          toast.success('Broker deleted successfully')
        } catch (error: any) {
          console.error('Error deleting broker:', error)
          toast.error(error.message || 'Failed to delete broker')
          throw error
        }
      },

      getBrokerByKey: (key) => {
        const { brokers } = get()
        return brokers.find(b => b.key === key)
      },
    }),
    { name: 'broker-store' }
  )
)
